import React, { useState } from 'react';
import './ChangePassword.css';

/**
 * Change Password Component
 * Allows admin to change their password from admin panel
 */
function ChangePassword() {
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear message when user types
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.old_password) {
      setMessage({ type: 'error', text: 'Current password is required' });
      return;
    }

    if (!formData.new_password) {
      setMessage({ type: 'error', text: 'New password is required' });
      return;
    }

    if (formData.new_password.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters long' });
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (formData.old_password === formData.new_password) {
      setMessage({ type: 'error', text: 'New password must be different from current password' });
      return;
    }

    setLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

      const response = await fetch(`${API_BASE_URL}/change-password.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: '✓ Password changed successfully!' });
        // Reset form
        setFormData({
          old_password: '',
          new_password: '',
          confirm_password: ''
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch (error) {
      console.error('Change password error:', error);
      setMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: '', color: '', text: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 'weak', color: '#dc3545', text: 'Weak' };
    if (strength <= 3) return { strength: 'medium', color: '#fdb208', text: 'Medium' };
    return { strength: 'strong', color: '#10b981', text: 'Strong' };
  };

  const passwordStrength = getPasswordStrength(formData.new_password);

  return (
    <div className="change-password-page">
      <div className="change-password-container">
        <div className="change-password-header">
          <h1 className="page-title">🔐 Change Password</h1>
          <p className="page-subtitle">Update your admin account password</p>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            <span>{message.text}</span>
            <button
              className="alert-close"
              onClick={() => setMessage({ type: '', text: '' })}
            >
              ×
            </button>
          </div>
        )}

        <div className="change-password-card">
          <form onSubmit={handleSubmit} className="password-form">
            {/* Current Password */}
            <div className="form-group">
              <label htmlFor="old_password" className="form-label">
                Current Password <span className="required">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showOldPassword ? 'text' : 'password'}
                  id="old_password"
                  name="old_password"
                  value={formData.old_password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your current password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                >
                  {showOldPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="form-group">
              <label htmlFor="new_password" className="form-label">
                New Password <span className="required">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new_password"
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter new password (min 8 characters)"
                  minLength="8"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {formData.new_password && (
                <div className="password-strength">
                  <div className="strength-bar">
                    <div
                      className={`strength-fill strength-${passwordStrength.strength}`}
                      style={{
                        width: passwordStrength.strength === 'weak' ? '33%' :
                               passwordStrength.strength === 'medium' ? '66%' : '100%'
                      }}
                    />
                  </div>
                  <span
                    className="strength-text"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.text}
                  </span>
                </div>
              )}
              <p className="form-hint">
                Use at least 8 characters with letters, numbers, and symbols
              </p>
            </div>

            {/* Confirm New Password */}
            <div className="form-group">
              <label htmlFor="confirm_password" className="form-label">
                Confirm New Password <span className="required">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirm_password"
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Re-enter new password"
                  minLength="8"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                <p className="form-error">Passwords do not match</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Changing Password...</span>
                  </>
                ) : (
                  <>
                    <span>🔒 Change Password</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Security Tips */}
          <div className="security-tips">
            <h3>🛡️ Security Tips</h3>
            <ul>
              <li>Use a unique password that you don't use elsewhere</li>
              <li>Mix uppercase and lowercase letters, numbers, and symbols</li>
              <li>Avoid using personal information</li>
              <li>Change your password regularly</li>
              <li>Never share your password with anyone</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangePassword;
